import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import { DropTarget } from 'react-dnd';
import _ from 'lodash';
import TaskEditorField, { getDefaultField, updateTaskDatesForCase } from './TaskEditorField';
import Select from './Select';
import Button from './Button';
import { isEmptyObject } from '../../validators/formValidators';
import { userWorkflowUpdateAction } from '../../actions/userActions';
import { caseSetServerError } from '../../actions/caseActions';
import { transformTextFromPlaceholderMapping,
         getWorkflowTranslationHashTable } from '../../helpers/utils';

import { formTarget,
         collectTarget } from '../../../client/dnd';

export const getNewId = (tasks) => {
    let maxId = tasks.reduce((a, b) => {
        if (_.isObject(a)) {
            a = a.id;
        }

        if (_.isObject(b)) {
            b = b.id;
        }

        return Math.max(Math.abs(a), Math.abs(b));
    }, 0);

    return - (maxId + 1);
}

export const validateTaskFields = (tasks) => {
    let noError = true;
    for (let i = 0; i < tasks.length; i++) {
        let task = tasks[i];
        noError = noError && validateTaskField(task);
    }

    return noError;
}

export const validateTaskField = (task) => {
    let empty = isEmptyObject(task.error) || task['$Deleted$'];
    return empty;
}

export const copyTasklistToCurrentCase = (newTasks, tasks, currentConfig, currentCase, user) => {
    let tasksLength = tasks.length;
    let placeholderMapping = getWorkflowTranslationHashTable(currentConfig, currentCase, {});
    let idMapping = {};
    for (let i = 0; i < newTasks.length; i++) {
        let newId = getNewId(tasks);
        idMapping[newTasks[i].id] = newId;
        newTasks[i].id = newId;
        newTasks[i]['$Editing$'] = false;
        newTasks[i]['$Modified$'] = true;
        newTasks[i]['CaseId'] = currentCase.id;
        newTasks[i]['Title'] = transformTextFromPlaceholderMapping(newTasks[i]['Title'], placeholderMapping);
        newTasks[i]['Description'] = transformTextFromPlaceholderMapping(newTasks[i]['Description'], placeholderMapping);
        if (!isEmptyObject(currentCase) && !isEmptyObject(user)) {
            updateTaskDatesForCase(currentCase, newTasks[i], user, false);
        }

        validateTaskField(newTasks[i]);
        tasks.push(newTasks[i]);
    }

    for (let i = 0; i < newTasks.length; i++) {
        if (tasks[i + tasksLength]['BlockedBy']) {
            tasks[i + tasksLength]['BlockedBy'] = idMapping[tasks[i + tasksLength]['BlockedBy']];
        }
    }

    return tasks;
}

class TaskEditor extends PureComponent {
    constructor(props) {
        super(props);
        this.onDelete = this.onDelete.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onDeleteTasks = this.onDeleteTasks.bind(this);
        this.onAdd = this.onAdd.bind(this);
        this.onChange = this.onChange.bind(this);
        this.findField = this.findField.bind(this);
        this.moveField = this.moveField.bind(this);
        this.collapseFields = this.collapseFields.bind(this);
        this.onTasklistChange = this.onTasklistChange.bind(this);
        this.getTasks = this.getTasks.bind(this);
        this.setTasks = this.setTasks.bind(this);
        this.state = {
            tasks: [],
            loading: false,
            editTasklistsOpen : false,
            tasklists: [],
            tasklistsLoading: false,
            selectedTasklistIndex: -1,
            selectedTaskTasklistIndex: -1,
        };
    }

    componentDidMount() {
        this.componentWillReceiveProps(this.props);
    }


    componentWillReceiveProps(nextProps) {
        if (nextProps.isSave && !this.props.isSave) {
            this.onSave();
        }

        if (!_.isEqual(this.props.currentCase, nextProps.currentCase)) {
            let tasks = _.cloneDeep(this.state.tasks);
            for (let i = 0; i < tasks.length; i++) {
                updateTaskDatesForCase(undefined, tasks[i], nextProps.loggedIn, false);
            }

            this.setState({tasks});
        }

        if (nextProps.tasklists && this.state.tasklists.length == 0) {
            if (nextProps.tasklists.length > 0) {
                if (this.state.selectedTasklistIndex < 0) {
                    this.setState({selectedTasklistIndex: 0});
                }

                if (this.state.selectedTaskTasklistIndex < 0) {
                    this.setState({selectedTaskTasklistIndex: 0});
                }
            } else {
                this.setState({selectedTasklistIndex: -1, selectedTaskTasklistIndex: -1});
            }

            let tasklists = _.cloneDeep(nextProps.tasklists);
            for (let i = 0; i < tasklists.length; i++) {
                for (let j = 0; j < tasklists[i].tasks.length; j++) {
                    delete tasklists[i].tasks[j]['CaseId'];
                }
            }

            this.setState({tasklists});
        }
    }

    onChange(task, modified = true) {
        let tasks = this.getTasks();
        let index = this.findField(task['id']).index;
        if (modified) {
            task['$Modified$'] = true;
        }

        tasks[index] = task;
        validateTaskFields(tasks);
        this.setTasks(tasks);
        this.forceUpdate();
        if (this.props.onChange) {
            this.props.onChange(tasks);
        }
    }

    collapseFields(task) {
        let tasks = this.getTasks().map(t => {
            return {
                ...t,
                '$Editing$':  task && t.id === task.id ? task['$Editing$'] : false,
            };
        });

        this.setTasks(tasks);
        if (this.props.onChange) {
            this.props.onChange(tasks);
        }
    }

    onTasklistChange(evt) {
        let type = evt.target.value;
        let selectedTasklistIndex = this.state.tasklists.findIndex(item => {
            return item.type == type;
        });

        this.setState({selectedTaskTasklistIndex: selectedTasklistIndex});
    }

    onDeleteTasks() {
        let tasks = this.getTasks();
        for (let i = 0; i < tasks.length; i++) {
            tasks[i]['$Deleted$'] = true;
            tasks[i]['$Modified$'] = true;
            this.setTasks(tasks);
        }
    }

    onSave() {
        let tasklists = _.cloneDeep(this.state.tasklists);
        for (let i = 0; i < tasklists.length; i++) {
            if (!validateTaskFields(tasklists[i].tasks)) {
                return;
            }

            tasklists[i].tasks = tasklists[i].tasks.filter(task => {
                return !task['$Deleted$'];

            }).map(task => {
                delete task['$Modified$'];
                delete task['$Editing$'];
                delete task['$Deleted$'];
                return task;
            });

        }

        this.setState({loading: true});
        let workflows = _.cloneDeep(this.props.currentConfig.workflow);
        for (let i = 0; i < workflows.length; i++) {
            let workflow = workflows[i];
            let index = tasklists.findIndex(t => {return t.type === workflow.type});
            let tasks = index != -1 ? tasklists[index].tasks : [];
            workflow.tasklist = tasks;
        }

        this.props.dispatch(userWorkflowUpdateAction(workflows))
            .then(result => {
                if (!result) {
                    console.error('Error while saving tasks/tasklists.');
                    this.props.dispatch(caseSetServerError('Error while saving tasks/tasklists.'));
                    return;
                }

                this.setState({loading: false});
                if (this.props.onSave) {
                    this.props.onSave();
                }
            })
            .catch(error => {
                console.error(new Error().stack, error);
                this.props.dispatch(caseSetServerError(error));
                this.setState({loading: false});
            });
    }

    onAdd(currentTask = undefined) {
        let tasks = this.getTasks();
        // Use negative id for newly added tasks
        let newId = getNewId(tasks);
        let task = getDefaultField(newId, this.props.loggedIn && this.props.loggedIn.id, this.props.caseId);
        task['$Modified$'] = true;
        if (currentTask === undefined) {
            tasks.push(task);
        } else {
            const index = tasks.findIndex(t => t.id === currentTask.id);
            tasks.splice(index + 1, 0, task);
        }

        this.setTasks(tasks);
    }


    onDelete(task) {
        let tasks = this.getTasks();
        let index = this.findField(task['id']).index;
        tasks[index]['$Deleted$'] = true;
        tasks[index]['$Modified$'] = true;
        validateTaskFields(tasks);
        this.setTasks(tasks);
    }


    getTasks(clone = true) {
        let tasks = [];
        if (this.state.selectedTaskTasklistIndex >= 0 && this.state.tasklists && this.state.tasklists.length > 0) {
            if (clone) {
                tasks = _.cloneDeep(this.state.tasklists[this.state.selectedTaskTasklistIndex].tasks);

            } else {
                tasks = this.state.tasklists[this.state.selectedTaskTasklistIndex].tasks;
            }
        }

        return tasks;
    }

    setTasks(tasks) {
        let tasklists = _.cloneDeep(this.state.tasklists);
        tasklists[this.state.selectedTaskTasklistIndex].tasks = tasks;
        this.setState({tasklists});
    }


    findField(id) {
        let tasks = this.getTasks(false);
        let index = tasks.findIndex(task => {
            return task['id'] == id;
        });

        if (index < 0) {
            return {
                task: {},
                index: -1
            }
        }

        let task = _.cloneDeep(tasks[index]);
        return {
            task,
            index
        };
    }

    moveField(draggedId, targetIndex) {
        const { task, index: sourceIndex } = this.findField(draggedId);
        let tasks = this.getTasks();

        // Swap tasks as well as their ids. From the database update it will look
        // like the dragged item overwrote stored item, so the final order by ids
        // will be maintained.
        let targetTaskOrderId = tasks[targetIndex]['OrderId'] || tasks[targetIndex]['id'];
        let sourceTaskOrderId = tasks[sourceIndex]['OrderId'] || tasks[sourceIndex]['id'];

        tasks.splice(sourceIndex, 1);
        tasks.splice(targetIndex, 0, task);

        tasks[sourceIndex]['OrderId'] = sourceTaskOrderId;
        tasks[sourceIndex]['$Modified$'] = true;
        tasks[targetIndex]['OrderId'] = targetTaskOrderId;
        tasks[targetIndex]['$Modified$'] = true;
        this.setTasks(tasks);
    }

    render() {
        const {
            connectDropTarget,
            tasklists,
        } = this.props;

        let tasks = this.getTasks(false);
        tasks = tasks.filter(task => (!task['$Deleted$']));
        const generatedFields = tasks.map(task =>
            (
                <TaskEditorField initialField={task}
                                 disabled={this.state.loading}
                                 onDelete={this.onDelete}
                                 onChange={this.onChange}
                                 onAdd={this.onAdd}
                                 key={`${task['id']}`}
                                 id={`${task['id']}`}
                                 isEmptyChronoError={false}
                                 tasks={tasks}
                                 findField={this.findField}
                                 moveField={this.moveField}
                                 collapseFields={this.collapseFields}
                                 caseType={this.state.selectedTaskTasklistIndex >= 0 ? this.state.tasklists[this.state.selectedTaskTasklistIndex].type : undefined}
                                 currentCase={undefined}
                                 renderGoToCaseButton={false}
                                 disableCheckBox />
            ));

        let tasklistTypes;
        if (!isEmptyObject(tasklists)) {
            tasklistTypes = tasklists.map((item) => {
                return item.type;
            });
        }

        let isLastFieldEdited = false;
        if (tasks && tasks.length > 0 && tasks[tasks.length - 1]['$Editing$']) {
            isLastFieldEdited = true;
        }

        const editor = (
            <div>
            <Row>
                <Col xs={12}>
                    <Select label="Task Template for Case Type"
                            field={{
                                name: 'Type',
                                onChange: this.onTasklistChange
                            }}
                            disabled={typeof(tasklistTypes) === 'undefined' || tasks === undefined || !validateTaskFields(tasks)}
                            loading={typeof(tasklistTypes) === 'undefined'}
                            options={tasklistTypes} />
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                {generatedFields}
                </Col>
            </Row>
            {!isLastFieldEdited && (
            <Row>
                <Col xs={12}>
                    <Button onClick={() => this.onAdd()}
                            block
                            disabled={tasks === undefined || this.state.selectedTasklistIndex < 0 || !validateTaskFields(tasks)}
                            icon="plus"
                            bsStyle='link'
                            iconStyle={{paddingBottom: '6px'}}
                            label="Add Task"
                            tooltip="Add new task"
                            tooltipPosition="bottom" />
                </Col>
            </Row>)}
            </div>
        );

        return connectDropTarget(editor);
    }
}

const mapStateToProps = (state) => {
    const config = state.user.config;
    const tasklists = [];
    if (config && config.workflow) {
        for (let i = 0; i < config.workflow.length; i++) {
            const workflow = config.workflow[i];
            tasklists.push({
                type: workflow.type,
                tasks: workflow.tasklist || []
            });
        }
    }

    return {
        tasklists,
        currentConfig: config,
        currentCase: state.cases.current,
        loggedIn: state.user.loggedIn,

        // React DnD:
        connectDropTarget: PropTypes.func.isRequired,
    };
}

export default _.flow(
    DropTarget('TASK_FIELD', formTarget, collectTarget),
    connect(mapStateToProps)
)(TaskEditor);
